import React, { useEffect, useState } from 'react';
import { OpModal } from 'components/customAntd/DLS/OpModal/OpModal';
import { AppDispatch, RootState } from 'store/store';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRedFlagLogs, fetchRedFlags } from 'store/slices/redFlagSlice';
import { RedFlag } from 'types/redFlagTypes';
import { formatFullName } from 'utils/utils';
import { OpTableRawColumnType } from 'components/customAntd/DLS/OpTableCore/OpTableCore';
import { StopOutlined, EyeOutlined } from "@ant-design/icons";
import { IOpTableProps, OpTable } from 'components/customAntd/DLS/OpTable/OpTable';
import { TABLE_HEIGHT } from 'constants/ui';
import dayjs from 'dayjs';
import { DATE_TIME_AM_PM_FORMAT } from 'constants/dates';
import RedFlagDetailsModal from './RedFlagDetailsModal';
import ExternalRedFlagDetailsModal from './ExternalRedFlagDetailsModal';
import { OpSpace } from 'components/customAntd/DLS/OpSpace/OpSpace';
import { useTranslation } from 'react-i18next';

interface RedFlagMatchModalProps {
    visible: boolean;
    onClose: () => void;
    externalRedFlags?: any[];
}

const RedFlagMatchModal: React.FC<RedFlagMatchModalProps> = ({ visible, onClose, externalRedFlags }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const { selectedRedFlaggedVisitor, matchingRedFlags, fetchRedFlagsLoading } = useSelector((state: RootState) => state.redFlags);
    const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
    const [selectedRedFlag, setSelectedRedFlag] = useState<RedFlag | null>(null);
    const [selectedExternalRecord, setSelectedExternalRecord] = useState<any>(null);
    const [isExternalDetailsVisible, setIsExternalDetailsVisible] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        if (orgId) {
            dispatch(fetchRedFlags({
                orgId,
                firstName: selectedRedFlaggedVisitor?.firstName || undefined,
                middleName: selectedRedFlaggedVisitor?.middleName || undefined,
                lastName: selectedRedFlaggedVisitor?.lastName || undefined,
                isMatchingRedFlags: true
            }));
        }
    }, [dispatch, orgId, selectedRedFlaggedVisitor?.firstName, selectedRedFlaggedVisitor?.middleName, selectedRedFlaggedVisitor?.lastName]);

    // Filter out expired red flags before transforming
    const now = dayjs();
    const activeRedFlags = matchingRedFlags.data.filter((item: RedFlag) => {
        if (!item.expireDate) return true; // No expireDate means never expires
        return dayjs.utc(item.expireDate).local().isAfter(now);
    });

    const transformedData = activeRedFlags.map((item: RedFlag) => ({
        ...item,
        effectiveDate: item.effectiveDate ? dayjs(item.effectiveDate).utc(true).local().format(DATE_TIME_AM_PM_FORMAT) : '--',
        fullName: formatFullName(item.firstName, item.middleName, item.lastName),
    }));

    const formatDate = (dateStr: string) => {
        if (!dateStr) return null;
        try {
            return dateStr.length === 8
                ? dateStr.replace(/^(\d{4})(\d{2})(\d{2})$/, '$1-$2-$3') // e.g. 19960600 will be converted to 1996-06-00
                : dateStr;
        } catch {
            return null;
        }
    };

    const columns: OpTableRawColumnType[] = [
        {
            dataIndex: 'levelName',
            label: t('visitors.redFlag.matchModal.columns.levelName'),
            render: (text: string) => {
                if (text === 'Blocklist') {
                    return <span><StopOutlined style={{ color: 'rgba(242,103,87,1.0)' }} /> {t('visitors.redFlag.matchModal.blocklist')}</span>;
                } else if (text === 'Watchlist') {
                    return <span><EyeOutlined style={{ color: '#FFD54F' }} /> {t('visitors.redFlag.matchModal.watchlist')}</span>;
                }
                return text;
            },
            // filter: {
            //     type: 'multiSelect',
            //     options: [
            //         { label: 'Blocklist', value: 'Blocklist' },
            //         { label: 'Watchlist', value: 'Watchlist' }
            //     ],
            // },
            sorter: (a, b) => (a.levelName || '').localeCompare(b.levelName || ''), // Alphabetical sorting with null check
        },
        {
            dataIndex: 'fullName',
            label: t('visitors.redFlag.matchModal.columns.fullName'),
            // filter: {
            //     type: 'input',
            // },
            sorter: (a, b) => (a.fullName || '').localeCompare(b.fullName || ''), // Alphabetical sorting with null check
        },
        {
            dataIndex: 'effectiveDate',
            label: t('visitors.redFlag.matchModal.columns.effectiveDate'),
            // filter: {
            //     type: 'input',
            // },
            sorter: (a, b) => (a.effectiveDate || '').localeCompare(b.effectiveDate || ''), // Alphabetical sorting with null check
        },
    ];

    const opTableProps: IOpTableProps = {
        dataSource: transformedData,
        columns: columns,
        rowActions: {
            onEditClick: (redFlag: RedFlag) => {
                setSelectedRedFlag(redFlag);
                dispatch(fetchRedFlagLogs({ orgId: orgId, redFlagId: redFlag.id }));
                setIsDetailsModalVisible(true);
            }
        },
        height: TABLE_HEIGHT,
        allowGlobalSearch: false,
        loading: fetchRedFlagsLoading,
        allowExport: false,
        allowShowHideColumns: false,
    };

    const externalColumns = [
        {
            label: t('visitors.redFlag.matchModal.columns.fullName'),
            dataIndex: ['person', 'full_name'],
            key: 'full_name',
            sorter: (a: any, b: any) => a.person.full_name.localeCompare(b.person.full_name),
        },
        {
            label: t('visitors.redFlag.matchModal.columns.dob'),
            dataIndex: ['person', 'dob'],
            key: 'dob',
            render: (dob: string) => dob ? formatDate(dob) : '--',
            sorter: (a: any, b: any) => a.person.dob.localeCompare(b.person.dob),
        },
        {
            label: t('visitors.redFlag.matchModal.columns.category'),
            dataIndex: 'category',
            key: 'category',
            sorter: (a: any, b: any) => a.category.localeCompare(b.category),
        },
    ];

    const externalTableProps: IOpTableProps = {
        dataSource: externalRedFlags,
        columns: externalColumns,
        rowActions: {
            onEditClick: (record) => {
                setSelectedExternalRecord(record);
                setIsExternalDetailsVisible(true);
            }
        },
        height: TABLE_HEIGHT,
        allowGlobalSearch: false,
        allowExport: false,
        allowShowHideColumns: false,
    };

    const hasMatchingRedFlags = matchingRedFlags.data.length > 0;
    const hasExternalRedFlags = externalRedFlags && externalRedFlags?.length > 0;

    const modalTitle = hasMatchingRedFlags && hasExternalRedFlags
        ? t('visitors.redFlag.matchModal.title')
        : hasMatchingRedFlags
            ? t('visitors.redFlag.matchModal.title')
            : t('visitors.redFlag.matchModal.externalTitle');

    return (
        <>
            <OpModal
                title={modalTitle}
                open={visible}
                onOk={onClose}
                onCancel={onClose}
                width={700}
                okText={t('visitors.redFlag.matchModal.close')}
                cancelButtonProps={{ style: { display: 'none' } }}
            >
                <OpSpace direction="vertical" style={{ overflowY: 'auto', maxHeight: '70vh', width: '100%' }}>
                    {hasMatchingRedFlags && <OpTable {...opTableProps} />}

                    {hasMatchingRedFlags && hasExternalRedFlags && <div style={{ marginTop: "16px" }} />}

                    {hasMatchingRedFlags && hasExternalRedFlags &&
                        <h4><b>{t('visitors.redFlag.matchModal.externalSectionTitle')}</b></h4>
                    }
                    {hasExternalRedFlags && <OpTable {...externalTableProps} />}
                </OpSpace>
            </OpModal >

            {isDetailsModalVisible && (
                <RedFlagDetailsModal
                    open={isDetailsModalVisible}
                    onClose={() => setIsDetailsModalVisible(false)}
                    redFlagData={selectedRedFlag}
                />
            )}

            {isExternalDetailsVisible && (
                <ExternalRedFlagDetailsModal
                    open={isExternalDetailsVisible}
                    onClose={() => setIsExternalDetailsVisible(false)}
                    record={selectedExternalRecord}
                />
            )}
        </>
    );
};

export default RedFlagMatchModal;
